import { useState } from 'react';

import { Link } from 'react-router';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import { Typography } from 'Components/Typography';

import { moduleSelector } from 'assets/content/landingPages/supplements';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Root = styled.section`
  display: flex;
  flex-wrap: wrap;
  width: 100vw;

  background-color: ${theme.colors.neutral[300]};
`;

const Header = styled.div`
  padding: 71.5px 24px 48px;

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 140px 72px 88px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 50%;
    height: 50vw;
    min-height: 754px;
    padding: 0 40px;

    border-bottom: 2px solid ${theme.colors.primary[400]};
  }
`;

const Content = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    width: 500px;
  }

  & a {
    ${legacyTheme.breakpoints.up('sm')} {
      margin: 0;

      font-family: ${legacyTheme.typography.simplonNorm};
      font-size: 16px;
      font-weight: normal;
      line-height: 0.8;
      text-transform: initial;
      letter-spacing: 0.44px;
    }
  }
`;

const Title = styled(Typography)`
  margin-bottom: 12px;

  font-size: 36px;
  line-height: 1.17;
  letter-spacing: normal;

  & span {
    position: relative;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 32px;
    font-size: 60px;
    letter-spacing: normal;

    & span {
      margin-right: 24px;
    }
  }

  & sup {
    top: -1.5em;
    font-size: 30%;
    font-weight: 500;
  }
`;

const Paragraph = styled(Typography)`
  letter-spacing: 0.5px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 32px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 24px;
  }

  & sup {
    font-size: 65%;
  }
`;

const IngredientsMobile = styled.div`
  width: 100%;
`;

const IngredientSelectors = styled.div`
  display: flex;
`;

const Selector = styled.button<{ opacity: number; selected: number }>`
  width: calc(100% / 3);
  padding: 0;

  opacity: ${(props) => props.opacity};

  &:nth-last-of-type(even) {
    border-top: 2px solid ${theme.colors.primary[400]};
    border-right: ${(props) =>
      props.selected === 1 ? `2px solid ${theme.colors.primary[400]}` : 'none'};
    border-bottom: 2px solid ${theme.colors.primary[400]};
    border-left: ${(props) =>
      props.selected === 1 ? `2px solid ${theme.colors.primary[400]}` : 'none'};
  }

  &:nth-last-of-type(odd) {
    border: 2px solid ${theme.colors.primary[400]};
  }

  &:first-of-type {
    border-right: ${(props) =>
      props.selected !== 1 ? `2px solid ${theme.colors.primary[400]}` : 'none'};
  }

  &:last-of-type {
    border-left: ${(props) =>
      props.selected !== 1 ? `2px solid ${theme.colors.primary[400]}` : 'none'};
  }
`;

const SelectorImage = styled.div<{ img: StaticImageData }>`
  width: 100%;
  height: 85px;

  background-image: ${({ img }) => `url(${img.src})`};
  background-size: cover;
  border-bottom: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    height: 158px;
  }
`;

const SelectorName = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 43px;
  padding-top: 2px;

  ${legacyTheme.breakpoints.up('sm')} {
    height: 79px;
  }
`;

const Ingredient = styled.div<{ img: StaticImageData; firstingredient?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 688px;
  padding-top: 48px;

  background-image: ${({ img }) => `url(${img.src})`};
  background-size: cover;
  background-position: ${(props) => (props.firstingredient ? 'bottom' : 'initial')};
  border-bottom: 2px solid ${theme.colors.primary[400]};

  a {
    font-family: ${legacyTheme.typography.simplonNorm};
    font-weight: normal;
    text-transform: capitalize;
    border: initial;

    :hover {
      border: initial;
    }
  }

  ${legacyTheme.breakpoints.up('sm')} {
    height: 805px;
    padding-top: 77px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: 50%;
    height: 50vw;
    min-height: 754px;
    padding-top: 72px;

    &:nth-last-of-type(odd) {
      border-left: 2px solid ${theme.colors.primary[400]};
    }
  }

  ${legacyTheme.breakpoints.up('lg')} {
    justify-content: center;
  }
`;

const IngredientName = styled(Typography)`
  margin-bottom: 18px;

  font-size: 24px;
  line-height: 1.33;
  letter-spacing: normal;
  text-align: center;

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 24px;
  }
`;

const IngredientImage = styled.img`
  width: 290px;
  height: auto;
  margin-bottom: 24px;

  ${legacyTheme.breakpoints.up('xs')} {
    width: 327px;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    width: 395px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: 370px;
    margin-bottom: 18px;
  }
`;

const IngredientDescription = styled(Typography)`
  width: 290px;

  line-height: 1.38;
  letter-spacing: 0.25px;
  text-align: center;

  ${legacyTheme.breakpoints.up('xs')} {
    width: 327px;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    width: 395px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: 370px;
    margin-bottom: 0;
  }
`;

export const ModuleSelector = () => {
  const { isMobile, isDesktop } = useResponsiveVariant();
  const [selected, setSelected] = useState(0);

  return (
    <Root data-testid="supplements-landing-module">
      <Header>
        <Content>
          <Title markupName="p" variant="p1">
            {moduleSelector.title}
          </Title>

          <Paragraph markupName="p" variant="p1">
            {moduleSelector.paragraph}
          </Paragraph>

          {!isMobile && (
            <Button
              Component={Link}
              data-testid="supplements-cta"
              to={moduleSelector.link.url}
              variant="underlinedBlack"
            >
              {moduleSelector.link.text}
            </Button>
          )}
        </Content>
      </Header>

      {!isDesktop && (
        <IngredientsMobile>
          <IngredientSelectors>
            {moduleSelector.ingredients.map((el, key) => (
              <Selector
                key={el.name}
                data-testid={`selector-ingredient-button-${key}`}
                onClick={() => setSelected(key)}
                opacity={key === selected ? 1 : 0.4}
                selected={selected}
              >
                <SelectorImage img={el.imageSelector.allDevice} />
                <SelectorName>{el.name}</SelectorName>
              </Selector>
            ))}
          </IngredientSelectors>

          <Ingredient
            data-testid={`selector-ingredient-${selected}`}
            firstingredient={selected === 0}
            img={moduleSelector.ingredients[selected]!.imageBackground.desktop}
          >
            <IngredientName markupName="p">
              {moduleSelector.ingredients[selected]!.name}
            </IngredientName>
            <IngredientImage
              alt={moduleSelector.ingredients[selected]!.imageModel.alt}
              src={moduleSelector.ingredients[selected]!.imageModel.desktop.src}
            />
            <IngredientDescription markupName="p" variant="p1">
              {moduleSelector.ingredients[selected]?.description}
            </IngredientDescription>
            <Button
              Component={Link}
              to={moduleSelector.ingredients[selected]!.link.url}
              variant="underlinedBlack"
            >
              {moduleSelector.ingredients[selected]!.link.text}
            </Button>
          </Ingredient>
        </IngredientsMobile>
      )}

      {isDesktop &&
        moduleSelector.ingredients.map((el) => (
          <Ingredient key={el.name} img={el.imageBackground.desktop}>
            <IngredientName markupName="p">{el.name}</IngredientName>
            <IngredientImage
              alt={moduleSelector.ingredients[selected]!.imageModel.alt}
              src={el.imageModel.desktop.src}
            />
            <IngredientDescription markupName="p">{el.description}</IngredientDescription>
            <Button Component={Link} to={el.link.url} variant="underlinedBlack">
              {el.link.text}
            </Button>
          </Ingredient>
        ))}
    </Root>
  );
};
